import { Sparkles } from 'lucide-react';
import { WidgetContainer } from '../../infrastructure/widgets/WidgetContainer';
import { WidgetConfig } from '../../infrastructure/contracts/widget.contract';
import { cn } from '@/shared/utils/utils';
import { useBriefing } from '@/features/ai/hooks/useAIAnalytics';

export const aiBriefingConfig: WidgetConfig = {
  id: 'dash-ai-briefing',
  title: 'AI Daily Briefing',
  category: 'timeline',
  description: 'Personalized insights for today',
  refreshStrategy: 'manual',
  defaultSize: { w: 6, h: 3 },
  priority: 1,
};

// Higher backend priority → more urgent → accent dot.
function dotTone(priority: number): string {
  if (priority >= 3) return 'bg-danger';
  if (priority === 2) return 'bg-warning';
  return 'bg-primary';
}

export function AIDailyBriefingWidget() {
  const { data, isLoading, isError, error, refetch } = useBriefing();

  const insights = (data ?? []).slice(0, 6);

  return (
    <WidgetContainer
      config={aiBriefingConfig}
      isLoading={isLoading}
      isError={isError}
      errorMessage={error?.message}
      onRefresh={() => {
        refetch();
      }}
    >
      {insights.length === 0 ? (
        <div className="flex flex-col items-center justify-center gap-2 py-8 text-center">
          <Sparkles className="h-6 w-6 text-on-surface-variant" />
          <p className="text-body-sm text-on-surface-variant">No insights for today yet.</p>
        </div>
      ) : (
        <ul className="flex flex-col gap-2.5">
          {insights.map((insight, i) => (
            <li key={i} className="flex items-start gap-3">
              <span className="mt-1.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-primary-container text-on-primary-container">
                <Sparkles className="h-3.5 w-3.5" />
              </span>
              <div className="min-w-0 flex-1">
                <p className="text-data-md leading-snug text-on-surface">{insight.text}</p>
              </div>
              <span
                className={cn('mt-2 h-2 w-2 shrink-0 rounded-full', dotTone(insight.priority))}
                aria-hidden
              />
            </li>
          ))}
        </ul>
      )}
    </WidgetContainer>
  );
}
